import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useService,
} from '../../../../../hooks';
import MedicalResultsService, {
  Result,
} from '../../../../../services/MedicalResultsService';
import { MeasurementDimension } from '../../Measurements/types';
import { ResponseStats } from '../../Measurements/useQueriedData';
import { medicalResultsMeasurementsMapper } from '../../utils/dynamicDataMappers/medical-results/measurements-mapper';
import { LabResultsConfigItem } from '../useLabResultsConfig';

const SERVICE = new MedicalResultsService();
const INCLUDE_CLINIC_ONLY = true;
const PAGE_SIZE = 200;
const RESULT_COUNT = '2';

export type UseFetchDimensionDataArgs = {
  patientId: string;
  labResultsConfigItem?: LabResultsConfigItem;
};

export type DimensionDatum = {
  id: string;
  dimension: MeasurementDimension;
  responseStats: ResponseStats;
  systems: fhir.Coding[];
};

export function useFetchDimensionData({
  patientId,
  labResultsConfigItem,
}: UseFetchDimensionDataArgs) {
  const [getResultsV3] = useService(SERVICE.getResultsV3);
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();

  const select = useCallback(
    (results: Result[]) => {
      const transformedData = medicalResultsMeasurementsMapper(
        results,
        labResultsConfigItem?.source,
      );
      const dimensions = transformedData.dimensions.map((dimension, index) => ({
        id: dimension.id,
        dimension,
        responseStats: transformedData.responses[index],
        systems: results[index].systems,
      }));
      const coding = labResultsConfigItem?.coding;
      if (coding) {
        const results = dimensions.filter(result =>
          result.systems.some(
            system =>
              system.code === coding.code && system.system === coding.system,
          ),
        );
        return results;
      }
      return dimensions;
    },
    [labResultsConfigItem],
  );

  return useQuery({
    queryKey: [
      'medical-results',
      'v3',
      patientId,
      'results',
      {
        projectId,
        cohortId,
      },
      labResultsConfigItem?.source,
    ],
    queryFn: async () => {
      const data = [];
      let nextPageToken = '';

      do {
        const response = await getResultsV3({
          patientId,
          project: projectId,
          cohort: cohortId,
          includeClinicOnly: INCLUDE_CLINIC_ONLY,
          pageSize: PAGE_SIZE,
          resultCount: RESULT_COUNT,
          nextPageToken,
          source: labResultsConfigItem?.source,
        });

        data.push(...response.data.items);

        const searchParams = new URLSearchParams(
          response.data.links.next?.split('?')[1] || '',
        );
        nextPageToken = searchParams.get('nextPageToken') || '';
      } while (nextPageToken);

      return data;
    },
    select,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}
